import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { getImageDimensionsForCrop } from '@utils/helpers/lib/imageRatio/imageRatio';
import { Button } from '../Button';

interface ImageCropperModalProps {
  isOpen: boolean;
  imageUrl: string;
  onClose: () => void;
  onCropComplete: (croppedFile: File) => void;
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  isOpen,
  imageUrl,
  onClose,
  onCropComplete
}) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState<Crop>({
    unit: 'px',
    width: 160,
    height: 160,
    x: 0,
    y: 0
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const getImageDimensionsAndSetCrop = useCallback(async () => {
    if (isOpen) {
      const image = imgRef.current;
      const dimensions = await getImageDimensionsForCrop(image);
      setCrop({
        unit: 'px',
        width: dimensions.size,
        height: dimensions.size,
        x: dimensions.center?.x || 0,
        y: dimensions.center?.y || 0
      });
      setCompletedCrop({
        unit: 'px',
        width: dimensions.size,
        height: dimensions.size,
        x: dimensions.center?.x || 0,
        y: dimensions.center?.y || 0
      });
    }
  }, [imageUrl, isOpen]);

  const getCroppedImg = async (): Promise<File | null> => {
    const image = imgRef.current;
    const canvas = document.createElement('canvas');

    if (!image || !completedCrop) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        if (!blob) {
          resolve(null);
          return;
        }
        const file = new File([blob], 'cropped-image.jpg', {
          type: 'image/jpeg'
        });
        resolve(file);
      }, 'image/jpeg');
    });
  };

  const handleCropConfirm = async () => {
    const croppedFile = await getCroppedImg();
    if (croppedFile) {
      onCropComplete(croppedFile);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10002]"
      onClick={e => e.stopPropagation()}>
      <div
        ref={modalRef}
        className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-lg relative"
        onClick={e => e.stopPropagation()}>
        {/* Close Button */}
        <Button
          btnStyle="link"
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
          onClick={onClose}>
          <SVGAssets.CloseIconGray className="w-3 h-3" />
        </Button>

        {/* Title */}
        <h2 className="text-lg font-semibold text-[#353535] mb-4">
          {t('Crop Your Image')}
        </h2>

        {/* Crop Area */}
        <div className="flex justify-center mb-4 max-h-[60vh] overflow-auto">
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={c => setCompletedCrop(c)}
            aspect={1}
            circularCrop
            className="h-full max-h-full flex items-center justify-center">
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Crop preview"
              className="max-w-full"
              onLoad={getImageDimensionsAndSetCrop}
            />
          </ReactCrop>
        </div>

        {/* Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
            className="bg-[#E9F6E6] text-[#7F9D48] font-medium px-5 py-2 rounded-md hover:bg-[#dff0dc] transition">
            {t('Cancel')}
          </Button>
          <Button
            onClick={e => {
              e.stopPropagation();
              handleCropConfirm();
            }}
            className="bg-[#7F9D48] text-white font-medium px-5 py-2 rounded-md hover:bg-[#6d8c3e] transition">
            {t('Confirm Crop')}
          </Button>
        </div>
      </div>
    </div>
  );
};
